import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import { DeleteIcon, EditPenIcon } from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { IconSize } from 'constants/coreDesignSystem';
import { PAGE_CODES } from 'constants/pageCodes';
import { PendingChangesActionType } from 'constants/pendingChanges';
import usePopover from 'hooks/usePopover';
import { getConditionalArray } from 'utils/data';

import { getMasterPermissionsToPage } from 'store/auth/selectors';
import { openConfirmationDialogAction, showMessageAction } from 'store/global/actions';
import { deleteDocumentExpirationDateAction } from 'store/underwriting/actions';

import { checkIsDefaultDocumentExpirationDate } from './DocumentExpirationDates.utils';

const highlightCustomStyles = {
  color: ({ palette }) => palette.text.tableHighlight,
};

const RowActionButton = ({ rowData, isLoading, setIsLoading, setDataToEdit, isEditMode }) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();

  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const { isManageAllowed, isEditAllowed } = useSelector(
    getMasterPermissionsToPage(PAGE_CODES.LOAN_UNDERWRITING),
  );

  const { id, title, documentExpirationDateType, pendingChangesActionType } = rowData;
  const isAdded = pendingChangesActionType === PendingChangesActionType.ADDED;
  const isDeleted = pendingChangesActionType === PendingChangesActionType.DELETED;
  const isDefaultDocumentExpirationDate = checkIsDefaultDocumentExpirationDate(
    documentExpirationDateType,
  );

  const onClickEdit = useCallback(() => {
    setDataToEdit(rowData);
  }, [rowData, setDataToEdit]);

  const onClickDelete = useCallback(() => {
    const onAccept = () => {
      setIsLoading(true);

      dispatch(
        deleteDocumentExpirationDateAction({
          applicationId,
          id,
          tabId: LoanApplicationTabId.UNDERWRITING,
        }),
      )
        .then(() =>
          dispatch(
            showMessageAction({
              message: `Row "${title}" has been successfully deleted`,
              variant: 'success',
            }),
          ),
        )
        .finally(() => setIsLoading(false));
    };

    dispatch(
      openConfirmationDialogAction({
        message: `Do you want to delete the row "${title}"?`,
        type: ConfirmationModalType.WARNING,
        onAcceptButtonName: 'Yes',
        onCancelButtonName: 'No',
        onAccept,
      }),
    );
  }, [dispatch, setIsLoading, applicationId, id, title]);

  const items = useMemo(
    () => [
      ...getConditionalArray(isEditAllowed, [
        {
          text: 'Edit',
          onClick: onClickEdit,
          icon: <EditPenIcon size={IconSize[20]} />,
          disabled: isLoading,
        },
      ]),

      ...getConditionalArray(isManageAllowed, [
        {
          text: 'Delete',
          onClick: onClickDelete,
          icon: <DeleteIcon size={IconSize[20]} />,
          disabled: isDefaultDocumentExpirationDate || isLoading,
        },
      ]),
    ],
    [
      onClickEdit,
      onClickDelete,
      isEditAllowed,
      isManageAllowed,
      isDefaultDocumentExpirationDate,
      isLoading,
    ],
  );

  return (
    <>
      <MoreButtonCDS
        customStyles={isAdded || isDeleted ? highlightCustomStyles : undefined}
        disabled={!items.length || isEditMode || isLoading}
        isHighlighted={isAdded}
        isSelected={!!anchorEl}
        onClick={onSetAnchorElement}
      />

      <MenuPopoverCDS
        anchorEl={anchorEl}
        items={items}
        onClick={onClearAnchorElement}
        onClose={onClearAnchorElement}
      />
    </>
  );
};

RowActionButton.propTypes = {
  rowData: PropTypes.object.isRequired,
  setDataToEdit: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool.isRequired,
  ...WithLoadingPropTypes,
};

export default withLoading(RowActionButton);
